import { supabase } from '../lib/supabase';
import { distributeAnnualAmount } from './agriEngine';

export type SourceModule = 'pme' | 'agri';

export interface ModuleSyncResult {
  success: boolean;
  budgetLinesUpdated: number;
  cashFlowLinesUpdated: number;
  error?: string;
}

async function ensureSystemCategory(
  userId: string,
  name: string,
  type: 'income' | 'expense',
  color: string
): Promise<string | null> {
  const { data: existing } = await supabase
    .from('categories')
    .select('id')
    .eq('user_id', userId)
    .eq('name', name)
    .maybeSingle();

  if (existing) return existing.id;

  const { data: created } = await supabase
    .from('categories')
    .insert({ user_id: userId, name, type, color, is_internal_transfer: false })
    .select('id')
    .maybeSingle();

  return created?.id ?? null;
}

function getExpenseOccurrencesForYear(
  amount: number,
  frequency: 'monthly' | 'quarterly' | 'yearly',
  nextPaymentDate: string,
  year: number
): Array<{ month: number; amount: number }> {
  const result: Array<{ month: number; amount: number }> = [];

  if (frequency === 'monthly') {
    for (let month = 1; month <= 12; month++) {
      result.push({ month, amount });
    }
  } else if (frequency === 'quarterly') {
    const startDate = new Date(nextPaymentDate);
    let startMonth = startDate.getFullYear() === year
      ? startDate.getMonth() + 1
      : ((startDate.getMonth() % 3) + 1);
    if (startMonth < 1) startMonth = 1;
    for (let month = startMonth; month <= 12; month += 3) {
      result.push({ month, amount });
    }
  } else if (frequency === 'yearly') {
    const date = new Date(nextPaymentDate);
    const month = date.getMonth() + 1;
    result.push({ month, amount });
  }

  return result;
}

export async function syncPMEToFinancial(userId: string): Promise<ModuleSyncResult> {
  let budgetLinesUpdated = 0;
  let cashFlowLinesUpdated = 0;

  try {
    const [{ data: invoices }, { data: expenses }] = await Promise.all([
      supabase.from('pme_invoices').select('*').eq('user_id', userId),
      supabase.from('pme_fixed_expenses').select('*').eq('user_id', userId).eq('is_active', true),
    ]);

    const [incomeCatId, expenseCatId] = await Promise.all([
      ensureSystemCategory(userId, 'Recettes clients (PME)', 'income', '#10B981'),
      ensureSystemCategory(userId, 'Charges fixes (PME)', 'expense', '#EF4444'),
    ]);

    await supabase
      .from('cash_flow_forecast_lines')
      .delete()
      .eq('user_id', userId)
      .eq('source_module', 'pme')
      .is('scenario_id', null);

    await supabase
      .from('budget_lines')
      .delete()
      .eq('user_id', userId)
      .eq('source_module', 'pme');

    const cashFlowLines: Record<string, unknown>[] = [];
    const currentYear = new Date().getFullYear();
    const yearsToSync = [currentYear, currentYear + 1];

    const invoicesByYear: Record<number, number> = {};
    for (const inv of invoices ?? []) {
      if (!incomeCatId) continue;
      const date = new Date(inv.expected_payment_date);
      const year = date.getFullYear();
      invoicesByYear[year] = (invoicesByYear[year] ?? 0) + inv.amount_ht;
      cashFlowLines.push({
        user_id: userId,
        forecast_year: year,
        forecast_month: date.getMonth() + 1,
        category_id: incomeCatId,
        description: `Facture – ${inv.client_name}`,
        amount: inv.amount_ht * (inv.probability / 100),
        type: 'income',
        source_module: 'pme',
        source_id: inv.id,
        source_type: 'pme_invoice',
        scenario_id: null,
      });
    }

    const annualExpenseTotal = (expenses ?? []).reduce((sum, e) => {
      switch (e.frequency) {
        case 'monthly': return sum + e.amount * 12;
        case 'quarterly': return sum + e.amount * 4;
        case 'yearly': return sum + e.amount;
        default: return sum;
      }
    }, 0);

    for (const exp of expenses ?? []) {
      if (!expenseCatId) continue;
      for (const year of yearsToSync) {
        const occurrences = getExpenseOccurrencesForYear(
          exp.amount, exp.frequency, exp.next_payment_date, year
        );
        for (const occ of occurrences) {
          cashFlowLines.push({
            user_id: userId,
            forecast_year: year,
            forecast_month: occ.month,
            category_id: expenseCatId,
            description: exp.label,
            amount: -occ.amount,
            type: 'expense',
            source_module: 'pme',
            source_id: exp.id,
            source_type: 'pme_expense',
            scenario_id: null,
          });
        }
      }
    }

    if (cashFlowLines.length > 0) {
      await supabase.from('cash_flow_forecast_lines').insert(cashFlowLines);
      cashFlowLinesUpdated = cashFlowLines.length;
    }

    const budgetLines: Record<string, unknown>[] = [];

    if (incomeCatId) {
      const invoiceYears = Object.keys(invoicesByYear).map(Number);
      const allInvoiceYears = invoiceYears.length > 0 ? invoiceYears : [currentYear];
      for (const year of allInvoiceYears) {
        budgetLines.push({
          user_id: userId,
          category_id: incomeCatId,
          year,
          planned_amount: invoicesByYear[year] ?? 0,
          source_module: 'pme',
          notes: 'Importé automatiquement depuis le module PME',
        });
      }
    }

    if (expenseCatId && annualExpenseTotal > 0) {
      for (const year of yearsToSync) {
        budgetLines.push({
          user_id: userId,
          category_id: expenseCatId,
          year,
          planned_amount: annualExpenseTotal,
          source_module: 'pme',
          notes: 'Importé automatiquement depuis le module PME',
        });
      }
    }

    if (budgetLines.length > 0) {
      await supabase.from('budget_lines').insert(budgetLines);
      budgetLinesUpdated = budgetLines.length;
    }

    return { success: true, budgetLinesUpdated, cashFlowLinesUpdated };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    return { success: false, budgetLinesUpdated, cashFlowLinesUpdated, error: msg };
  }
}

export async function syncAgriToFinancial(userId: string, year: number): Promise<ModuleSyncResult> {
  let budgetLinesUpdated = 0;
  let cashFlowLinesUpdated = 0;

  try {
    const [
      { data: campaigns },
      { data: subsidies },
      { data: herds },
      { data: equipment },
    ] = await Promise.all([
      supabase.from('agri_campaigns').select('*').eq('user_id', userId).eq('campaign_year', year),
      supabase.from('agri_subsidies').select('*').eq('user_id', userId).eq('campaign_year', year).neq('status', 'annule'),
      supabase.from('agri_livestock_herds').select('*').eq('user_id', userId),
      supabase.from('agri_equipment').select('*').eq('user_id', userId).in('status', ['finance', 'acquis']),
    ]);

    const campaignIds = (campaigns ?? []).map(c => c.id);
    const herdIds = (herds ?? []).map(h => h.id);

    const [
      { data: campaignCharges },
      { data: livestockCharges },
      { data: livestockSales },
    ] = await Promise.all([
      campaignIds.length > 0
        ? supabase.from('agri_campaign_charges').select('*').in('campaign_id', campaignIds)
        : Promise.resolve({ data: [] as Record<string, unknown>[] }),
      herdIds.length > 0
        ? supabase.from('agri_livestock_charges').select('*').in('herd_id', herdIds).eq('campaign_year', year)
        : Promise.resolve({ data: [] as Record<string, unknown>[] }),
      herdIds.length > 0
        ? supabase.from('agri_livestock_sales').select('*').in('herd_id', herdIds).eq('campaign_year', year).neq('status', 'annule')
        : Promise.resolve({ data: [] as Record<string, unknown>[] }),
    ]);

    const [
      cropIncomeCatId,
      cropExpenseCatId,
      subsidyCatId,
      livestockIncomeCatId,
      livestockExpenseCatId,
      equipmentCatId,
    ] = await Promise.all([
      ensureSystemCategory(userId, 'Recettes cultures (Agri)', 'income', '#22C55E'),
      ensureSystemCategory(userId, 'Charges cultures (Agri)', 'expense', '#F97316'),
      ensureSystemCategory(userId, 'Subventions agricoles (Agri)', 'income', '#F59E0B'),
      ensureSystemCategory(userId, 'Ventes élevage (Agri)', 'income', '#3B82F6'),
      ensureSystemCategory(userId, 'Charges élevage (Agri)', 'expense', '#6B7280'),
      ensureSystemCategory(userId, 'Matériel agricole (Agri)', 'expense', '#64748B'),
    ]);

    await supabase
      .from('cash_flow_forecast_lines')
      .delete()
      .eq('user_id', userId)
      .eq('source_module', 'agri')
      .eq('forecast_year', year)
      .is('scenario_id', null);

    await supabase
      .from('budget_lines')
      .delete()
      .eq('user_id', userId)
      .eq('source_module', 'agri')
      .eq('year', year);

    const cashFlowLines: Record<string, unknown>[] = [];
    let totalCropIncome = 0;
    let totalCropExpense = 0;
    let totalSubsidies = 0;
    let totalLivestockIncome = 0;
    let totalLivestockExpense = 0;
    let totalEquipmentExpense = 0;

    for (const campaign of campaigns ?? []) {
      const production = (campaign.surface_ha * campaign.target_yield_qtx_ha) / 10;
      const revenue = production * campaign.target_price_eur_t;
      totalCropIncome += revenue;

      if (cropIncomeCatId && revenue > 0) {
        const harvestDate = campaign.harvest_date
          ? new Date(campaign.harvest_date)
          : new Date(year, 7, 1);
        cashFlowLines.push({
          user_id: userId,
          forecast_year: harvestDate.getFullYear(),
          forecast_month: harvestDate.getMonth() + 1,
          category_id: cropIncomeCatId,
          description: `Récolte – ${campaign.name}`,
          amount: revenue,
          type: 'income',
          source_module: 'agri',
          source_id: campaign.id,
          source_type: 'agri_campaign_income',
          scenario_id: null,
        });
      }
    }

    for (const charge of campaignCharges ?? []) {
      totalCropExpense += Number(charge.amount);
      if (cropExpenseCatId) {
        const date = charge.charge_date
          ? new Date(charge.charge_date as string)
          : new Date(year, 3, 1);
        cashFlowLines.push({
          user_id: userId,
          forecast_year: date.getFullYear(),
          forecast_month: date.getMonth() + 1,
          category_id: cropExpenseCatId,
          description: charge.label as string,
          amount: -Number(charge.amount),
          type: 'expense',
          source_module: 'agri',
          source_id: charge.id as string,
          source_type: 'agri_campaign_charge',
          scenario_id: null,
        });
      }
    }

    for (const subsidy of subsidies ?? []) {
      totalSubsidies += Number(subsidy.amount);
      if (subsidyCatId) {
        const payDate = subsidy.theoretical_payment_date
          ? new Date(subsidy.theoretical_payment_date as string)
          : subsidy.prudent_payment_date
            ? new Date(subsidy.prudent_payment_date as string)
            : new Date(year, 9, 1);
        cashFlowLines.push({
          user_id: userId,
          forecast_year: payDate.getFullYear(),
          forecast_month: payDate.getMonth() + 1,
          category_id: subsidyCatId,
          description: subsidy.name as string,
          amount: Number(subsidy.amount),
          type: 'income',
          source_module: 'agri',
          source_id: subsidy.id as string,
          source_type: 'agri_subsidy',
          scenario_id: null,
        });
      }
    }

    for (const sale of livestockSales ?? []) {
      const amount = Number(sale.quantity) * Number(sale.unit_price);
      totalLivestockIncome += amount;
      if (livestockIncomeCatId && amount > 0) {
        const date = sale.planned_date
          ? new Date(sale.planned_date as string)
          : new Date(year, 5, 1);
        cashFlowLines.push({
          user_id: userId,
          forecast_year: date.getFullYear(),
          forecast_month: date.getMonth() + 1,
          category_id: livestockIncomeCatId,
          description: sale.label as string,
          amount,
          type: 'income',
          source_module: 'agri',
          source_id: sale.id as string,
          source_type: 'agri_livestock_sale',
          scenario_id: null,
        });
      }
    }

    for (const charge of livestockCharges ?? []) {
      const annual = Number(charge.annual_amount);
      totalLivestockExpense += annual;
      if (livestockExpenseCatId && annual > 0) {
        const monthlyAmounts = distributeAnnualAmount(annual, charge.monthly_pattern as 'uniform' | 'winter_heavy');
        for (let i = 0; i < 12; i++) {
          if (monthlyAmounts[i] > 0) {
            cashFlowLines.push({
              user_id: userId,
              forecast_year: year,
              forecast_month: i + 1,
              category_id: livestockExpenseCatId,
              description: charge.label as string,
              amount: -monthlyAmounts[i],
              type: 'expense',
              source_module: 'agri',
              source_id: charge.id as string,
              source_type: 'agri_livestock_charge',
              scenario_id: null,
            });
          }
        }
      }
    }

    for (const equip of equipment ?? []) {
      const monthly = Number(equip.monthly_payment);
      if (monthly <= 0) continue;
      totalEquipmentExpense += monthly * 12;
      if (equipmentCatId) {
        for (let month = 1; month <= 12; month++) {
          cashFlowLines.push({
            user_id: userId,
            forecast_year: year,
            forecast_month: month,
            category_id: equipmentCatId,
            description: `Crédit – ${equip.name}`,
            amount: -monthly,
            type: 'expense',
            source_module: 'agri',
            source_id: equip.id as string,
            source_type: 'agri_equipment',
            scenario_id: null,
          });
        }
      }
    }

    if (cashFlowLines.length > 0) {
      await supabase.from('cash_flow_forecast_lines').insert(cashFlowLines);
      cashFlowLinesUpdated = cashFlowLines.length;
    }

    const budgetEntries: Array<{ catId: string | null; amount: number; label: string }> = [
      { catId: cropIncomeCatId, amount: totalCropIncome, label: 'Recettes cultures' },
      { catId: cropExpenseCatId, amount: totalCropExpense, label: 'Charges cultures' },
      { catId: subsidyCatId, amount: totalSubsidies, label: 'Subventions' },
      { catId: livestockIncomeCatId, amount: totalLivestockIncome, label: 'Ventes élevage' },
      { catId: livestockExpenseCatId, amount: totalLivestockExpense, label: 'Charges élevage' },
      { catId: equipmentCatId, amount: totalEquipmentExpense, label: 'Matériel' },
    ];

    const budgetLines: Record<string, unknown>[] = [];
    for (const { catId, amount } of budgetEntries) {
      if (catId && amount > 0) {
        budgetLines.push({
          user_id: userId,
          category_id: catId,
          year,
          planned_amount: amount,
          source_module: 'agri',
          notes: 'Importé automatiquement depuis le module Agriculture',
        });
      }
    }

    if (budgetLines.length > 0) {
      await supabase.from('budget_lines').insert(budgetLines);
      budgetLinesUpdated = budgetLines.length;
    }

    return { success: true, budgetLinesUpdated, cashFlowLinesUpdated };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : 'Erreur inconnue';
    return { success: false, budgetLinesUpdated, cashFlowLinesUpdated, error: msg };
  }
}
